import React, { useState, useEffect } from 'react';
import { CenteredModal } from '~/components';
import permissionRequestApi from '~/api/permissionRequestApi';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationTriangle, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';

export default function ReviewUserRequestsModal({ show, onClose, userId, userName, onSuccess, status = 0 }) {
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [requests, setRequests] = useState([]);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    useEffect(() => {
        if (show && userId) {
            fetchRequests();
            setError('');
            setSuccessMsg('');
        } else {
            setRequests([]);
        }
    }, [show, userId]);

    const isViewMode = status !== 0;

    const fetchRequests = async () => {
        setLoading(true);
        try {
            console.log("Calling API to get requests with userId:", userId, "status:", status);
            const res = status === 0 
                ? await permissionRequestApi.getUserPendingRequests(userId)
                : await permissionRequestApi.getUserRequestsByStatus(userId, status);
            console.log("API Response:", res);
            
            const isSucceeded = res.data?.succeeded || res.data?.Succeeded || true;
            const responseData = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.Data);

            if (isSucceeded && Array.isArray(responseData)) {
                // Sort requests to group by prefix and put .Access first
                const sortedData = [...responseData].sort((a, b) => {
                    const aParts = a.RequestedPermission.split('.');
                    const bParts = b.RequestedPermission.split('.');
                    
                    const aAction = aParts.pop();
                    const aPrefix = aParts.join('.');
                    
                    const bAction = bParts.pop();
                    const bPrefix = bParts.join('.');
                    
                    if (aPrefix < bPrefix) return -1;
                    if (aPrefix > bPrefix) return 1;
                    
                    if (aAction === 'Access' && bAction !== 'Access') return -1;
                    if (aAction !== 'Access' && bAction === 'Access') return 1;
                    
                    if (aAction < bAction) return -1;
                    if (aAction > bAction) return 1;
                    return 0;
                });

                const mapped = sortedData.map(r => ({
                    ...r,
                    Action: r.Status === 1 ? 'Approve' : (r.Status === 2 ? 'Reject' : 'Approve'),
                    RejectReason: r.RejectReason || ''
                }));
                setRequests(mapped);
            } else {
                console.error("Failed condition. isSucceeded:", isSucceeded, "responseData:", responseData);
                setError('Failed to load user requests.');
            }
        } catch (err) {
            console.error("API Error caught:", err);
            setError('Failed to load user requests. ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const handleActionChange = (id, action) => {
        setRequests(prev => {
            const target = prev.find(r => r.Id === id);
            if (!target) return prev;

            const isAccess = target.RequestedPermission.endsWith('.Access');
            const prefixParts = target.RequestedPermission.split('.');
            prefixParts.pop(); // Remove the action part (Access, Edit, Create, etc.)
            const prefix = prefixParts.join('.');
            const parentAccess = `${prefix}.Access`;

            return prev.map(r => {
                // 1. Update the target itself
                if (r.Id === id) {
                    return { ...r, Action: action, RejectReason: action === 'Approve' ? '' : r.RejectReason };
                }

                // 2. Rule: Rejecting Parent (.Access) auto-rejects all Children
                if (action === 'Reject' && isAccess) {
                    // Check if this row is a child of the rejected parent
                    if (r.RequestedPermission.startsWith(`${prefix}.`) && r.RequestedPermission !== target.RequestedPermission) {
                        return { ...r, Action: 'Reject', RejectReason: r.RejectReason || 'Parent access denied' };
                    }
                }

                // 3. Rule: Approving Child (Not .Access) auto-approves the Parent
                if (action === 'Approve' && !isAccess) {
                    // Check if this row is the parent
                    if (r.RequestedPermission === parentAccess) {
                        return { ...r, Action: 'Approve', RejectReason: '' };
                    }
                }

                return r;
            });
        });
    };

    const handleReasonChange = (id, reason) => {
        setRequests(prev => prev.map(r => r.Id === id ? { ...r, RejectReason: reason } : r));
    };

    const handleSubmit = async () => {
        // Validate
        const hasError = requests.some(r => r.Action === 'Reject' && r.RejectReason.trim() === '');
        if (hasError) {
            setError('Please provide a reject reason for all rejected requests.');
            return;
        }

        setError('');
        setSubmitting(true);

        const payload = requests.map(r => ({
            RequestId: r.Id,
            Action: r.Action,
            RejectReason: r.Action === 'Reject' ? r.RejectReason : null
        }));

        try {
            const res = await permissionRequestApi.resolveBulk(payload);
            const isSucceeded = res.data?.succeeded || res.data?.Succeeded;
            if (isSucceeded) {
                setSuccessMsg('Successfully resolved requests!');
                setTimeout(() => {
                    if (onSuccess) onSuccess();
                    onClose();
                }, 5000);
            } else {
                const message = res.data?.message || res.data?.Message || 'Failed to resolve requests.';
                setError(message);
            }
        } catch (err) {
            setError('An error occurred while resolving requests.');
        } finally {
            setSubmitting(false);
        }
    };

    const isAllValid = !requests.some(r => r.Action === 'Reject' && r.RejectReason.trim() === '');

    return (
        <CenteredModal 
            show={show} 
            onClose={onClose} 
            title={isViewMode ? `View Requests for ${userName}` : `Review Requests for ${userName}`}
            modalType="modal-xl"
            onConfirm={isViewMode ? undefined : handleSubmit}
            confirmText="Submit Request"
            closeText={isViewMode ? 'Close' : 'Cancel'}
            isSubmitting={submitting}
            disabledConfirmBtn={!isAllValid}
        >
            <div className="position-relative">
                {error && (
                    <div className="alert alert-danger d-flex align-items-center mb-4 mx-4 mt-3 py-2" role="alert" style={{ fontSize: '0.875rem' }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                        <div>{error}</div>
                    </div>
                )}

                {successMsg && (
                    <div className="alert alert-success d-flex align-items-center mb-4 mx-4 mt-3 py-2" role="alert" style={{ fontSize: '0.875rem' }}>
                        <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                        <div>{successMsg}</div>
                    </div>
                )}

                {loading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : (
                    <div className="mx-4 mt-4 mb-4">
                        <div className="mb-3">
                            <p style={{ margin: 0, fontSize: '0.85rem', color: '#687078' }}>
                                List of pending permission requests. Choose to <span className="fw-bold text-success">Approve</span> or <span className="fw-bold text-danger">Reject</span> each request.
                            </p>
                        </div>
                        <div className="table-responsive border rounded flex-grow-1 shadow-sm" style={{ overflowY: "auto", overflowX: "auto", maxHeight: "400px" }}>
                            <table className="table table-hover table-striped mb-0 fs-9" style={{ minWidth: "900px" }}>
                                <thead className="table-light sticky-top">
                                    <tr>
                                        <th style={{ width: '8%' }} className="ps-3 align-middle py-2 text-center">No.</th>
                                        <th style={{ width: '25%' }} className="align-middle py-2">Requested Permission</th>
                                        <th style={{ width: '30%' }} className="align-middle py-2">Reason</th>
                                        <th style={{ width: '12%' }} className="align-middle py-2 text-center">Action</th>
                                        <th style={{ width: '25%' }} className="align-middle py-2">Reject Reason <span className="text-danger">*</span></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {requests.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="text-center text-muted py-4">No pending requests found.</td>
                                        </tr>
                                    ) : (
                                        requests.map((r, i) => {
                                            const isAccess = r.RequestedPermission.endsWith('.Access');
                                            const prefixParts = r.RequestedPermission.split('.');
                                            prefixParts.pop();
                                            const prefix = prefixParts.join('.');
                                            const hasAccessSibling = requests.some(req => req.RequestedPermission === `${prefix}.Access`);
                                            
                                            const isParent = isAccess || !hasAccessSibling;

                                            return (
                                            <tr key={r.Id} className={`align-middle ${isParent ? 'fw-bold' : ''}`} style={{ backgroundColor: isParent ? '#f8f9fa' : 'transparent' }}>
                                                <td className="text-center fw-bold ps-3">{i + 1}</td>
                                                <td>
                                                    {!isParent ? (
                                                        <div className="d-flex align-items-center ms-4">
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#aab7b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2 mt-1">
                                                                <polyline points="9 18 15 12 9 6"></polyline>
                                                            </svg>
                                                            <code>{r.RequestedPermission}</code>
                                                        </div>
                                                    ) : (
                                                        <div className="d-flex align-items-center">
                                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#687078" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2">
                                                                <rect x="3" y="3" width="7" height="7"></rect>
                                                                <rect x="14" y="3" width="7" height="7"></rect>
                                                                <rect x="14" y="14" width="7" height="7"></rect>
                                                                <rect x="3" y="14" width="7" height="7"></rect>
                                                            </svg>
                                                            <code className="fw-bold" style={{ fontSize: '13px' }}>{r.RequestedPermission}</code>
                                                        </div>
                                                    )}
                                                </td>
                                                <td><div className="text-muted text-break" style={{ fontSize: "0.85rem" }}>{r.Reason}</div></td>
                                                <td className="text-center">
                                                    {isViewMode ? (
                                                        <span className={`badge ${r.Action === 'Approve' ? 'bg-success' : 'bg-danger'}`}>
                                                            {r.Action === 'Approve' ? 'Approved' : 'Rejected'}
                                                        </span>
                                                    ) : (
                                                        <div className="d-flex align-items-center justify-content-center gap-2" style={{ whiteSpace: 'nowrap' }}>
                                                            <span 
                                                                className={`fw-bold cursor-pointer transition-base ${r.Action === 'Reject' ? 'text-danger' : 'text-muted'}`} 
                                                                style={{ fontSize: '11px', userSelect: 'none' }}
                                                                onClick={() => handleActionChange(r.Id, 'Reject')}
                                                            >
                                                                Reject
                                                            </span>
                                                            <div className="form-check form-switch mb-0 p-0 d-flex align-items-center justify-content-center">
                                                                <input 
                                                                    className="form-check-input m-0 shadow-none cursor-pointer" 
                                                                    type="checkbox" 
                                                                    role="switch"
                                                                    style={{ 
                                                                        width: '32px', 
                                                                        height: '16px',
                                                                        backgroundColor: r.Action === 'Approve' ? '#198754' : '#dc3545',
                                                                        borderColor: r.Action === 'Approve' ? '#198754' : '#dc3545',
                                                                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e")`
                                                                    }}
                                                                    checked={r.Action === 'Approve'} 
                                                                    onChange={(e) => handleActionChange(r.Id, e.target.checked ? 'Approve' : 'Reject')} 
                                                                />
                                                            </div>
                                                            <span 
                                                                className={`fw-bold cursor-pointer transition-base ${r.Action === 'Approve' ? 'text-success' : 'text-muted'}`} 
                                                                style={{ fontSize: '11px', userSelect: 'none' }}
                                                                onClick={() => handleActionChange(r.Id, 'Approve')}
                                                            >
                                                                Approve
                                                            </span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="pe-3">
                                                    <textarea 
                                                        className={`form-control form-control-sm ${r.Action === 'Reject' && r.RejectReason.trim() === '' ? 'border-danger' : ''}`} 
                                                        placeholder={r.Action === 'Approve' ? 'Not required for approval' : 'Enter rejection reason...'}
                                                        value={r.RejectReason}
                                                        onChange={(e) => handleReasonChange(r.Id, e.target.value)}
                                                        disabled={isViewMode || r.Action === 'Approve'}
                                                        rows="2"
                                                        style={{ fontSize: "0.85rem", resize: 'vertical', minHeight: '38px', backgroundColor: (isViewMode || r.Action === 'Approve') ? '#f8f9fa' : '#ffffff' }}
                                                    />
                                                </td>
                                            </tr>
                                        )})
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </CenteredModal>
    );
}
